import { cssThemedValue, Spinner } from '@creditornot/cb-ingredients';
import { ModalHeader } from '@creditornot/cb-modal';
import { Empty } from '@creditornot/cb-placeholders';
import { SegmentedControl } from '@creditornot/cb-segmented-control';
import { Tag, TagVariant } from '@creditornot/cb-tag';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React, { useContext, useState } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import styled from 'styled-components';

import { APIErrorNotification, WoltModal } from 'common/components';
import { Typography as T } from 'common/typography';
import { MenuMetaContext } from 'modules/menu/MenuMetaContext';

import { fetchMenuPulls, fetchMenuPushes } from './api';
import { Intent, SyncIntentState } from './types';
import { isIntentScheduled } from './useMenuSyncStatus';

type Props = {
  initialMode: 'push' | 'pull';
  onClose: () => void;
};

const Root = styled(WoltModal)`
  width: 640px;
  border-radius: 8px;
  background-color: ${cssThemedValue('surfaceMain')};
  display: flex;
  flex-direction: column;
`;

const ModeToggleContainer = styled.div`
  display: flex;
  width: 100%;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
  padding: 16px 24px;
`;

const VenueList = styled.div`
  background: ${cssThemedValue('surfaceMain')};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  flex: 1 1 auto;
  overflow-y: auto;
  max-height: 60vh;
`;

const VenueItem = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 8px 24px;
  flex-shrink: 0;
  background-color: ${cssThemedValue('surfaceMain')};
  transition: background-color 100ms;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  & + & {
    border-block-start: 1px solid ${cssThemedValue('borderPrimary')};
  }
`;

const NameAndTimeStampContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const VenueName = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-block-end: 4px;
`;

const intentStateToTagVariantMap: Record<SyncIntentState | 'scheduled', TagVariant> = {
  failure: 'red',
  pending: 'lightBlack',
  revoked: 'lightOrange',
  started: 'lightGreen',
  success: 'green',
  scheduled: 'lightBlue',
};

const intentStateToPhraseKey: Record<SyncIntentState | 'scheduled', string> = {
  failure: 'menu.menu-sync.state.failure',
  pending: 'menu.menu-sync.state.pending',
  revoked: 'menu.menu-sync.state.revoked',
  started: 'menu.menu-sync.state.started',
  success: 'menu.menu-sync.state.success',
  scheduled: 'menu.menu-sync.state.scheduled',
};

const ListContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const EmptyListContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px;
`;

const IntentList = ({ intents, intl, mode }: { intents: Intent[]; intl: IntlShape; mode: 'push' | 'pull' }) => {
  if (intents.length === 0) {
    return (
      <EmptyListContainer data-testid={mode === 'pull' ? 'empty-pulls-list' : 'empty-pushes-list'}>
        <Empty
          title={intl.formatMessage({
            id: mode === 'pull' ? 'menu.menu-sync.no-recent-pulls' : 'menu.menu-sync.no-recent-pushes',
          })}
        />
      </EmptyListContainer>
    );
  }

  return (
    <ListContainer data-testid="intent-list-container">
      {intents.map(intent => (
        <VenueItem key={intent.id}>
          <NameAndTimeStampContainer>
            <VenueName>
              <T.Body3>{intent.venue_name[0].value}</T.Body3>
            </VenueName>
            <T.Small>{DateTime.fromISO(intent.updated_at).toLocaleString(DateTime.DATETIME_SHORT)}</T.Small>
          </NameAndTimeStampContainer>
          <StatusTag intent={intent} />
        </VenueItem>
      ))}
    </ListContainer>
  );
};

const StatusTag = ({ intent }: { intent: Intent }) => {
  const intentState = isIntentScheduled(intent.scheduled_at) && intent.state === 'pending' ? 'scheduled' : intent.state;

  return (
    <Tag variant={intentStateToTagVariantMap[intentState]} uppercase size="tiny">
      <FormattedMessage id={intentStateToPhraseKey[intentState]} />
      {intentState === 'scheduled' &&
        ` - ${DateTime.fromISO(intent.scheduled_at).toLocaleString(DateTime.DATETIME_SHORT)}`}
    </Tag>
  );
};

const SyncHistoryModal = ({ initialMode, onClose }: Props) => {
  const { menuId } = useContext(MenuMetaContext);

  const intl = useIntl();
  const [mode, setMode] = useState<'push' | 'pull'>(initialMode);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);

  const {
    data: pastPushes,
    isFetching: fetchingPastPushes,
    error: pushesError,
  } = useQuery(
    ['past-menu-pushes', menuId],
    /*
      Temporary page size of 500, so that 99.99% of the time we don't need pagination
      TODO: add infinite list pagination
    */
    async () => fetchMenuPushes(menuId, nextPageToken, [], 500),
    {
      onSuccess: data => {
        setNextPageToken(data.next_push_page_token);
      },
      enabled: mode === 'push',
      retry: false,
    },
  );

  const {
    data: pastPulls,
    isFetching: fetchingPastPulls,
    error: pullsError,
  } = useQuery(
    ['past-menu-pulls', menuId],
    /*
      Temporary page size of 500, so that 99.99% of the time we don't need pagination
      TODO: add infinite list pagination
    */
    async () => fetchMenuPulls(menuId, nextPageToken, [], 500),
    {
      onSuccess: data => {
        setNextPageToken(data.next_pull_page_token);
      },
      enabled: mode === 'pull',
      retry: false,
    },
  );

  return (
    <Root isOpen onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="menu.menu-sync.recent-syncs" />
      </ModalHeader>
      <ModeToggleContainer>
        <SegmentedControl<'push' | 'pull'>
          disabled={fetchingPastPushes || fetchingPastPulls}
          options={[
            { label: intl.formatMessage({ id: 'menu.menu-sync.pushes' }), value: 'push' },
            { label: intl.formatMessage({ id: 'menu.menu-sync.pulls' }), value: 'pull' },
          ]}
          value={mode}
          onChange={value => setMode(value)}
        />
      </ModeToggleContainer>
      <VenueList>
        {(fetchingPastPushes || fetchingPastPulls) && (
          <EmptyListContainer>
            <Spinner size="large" />
          </EmptyListContainer>
        )}

        {(pullsError || pushesError) && (
          <EmptyListContainer>
            <APIErrorNotification error={pullsError ?? pushesError} style={{ width: '100%' }} />
          </EmptyListContainer>
        )}

        {!!pastPushes && mode === 'push' && <IntentList intents={pastPushes.push_intents} intl={intl} mode={mode} />}

        {!!pastPulls && mode === 'pull' && <IntentList intents={pastPulls.pull_intents} intl={intl} mode={mode} />}
      </VenueList>
    </Root>
  );
};

export default SyncHistoryModal;
